import { useSurveyAuditsBySubject, useStartAudit, useArchiveAudit } from './useSurveyAudit';
import { SurveyAuditTableData } from '../types';
import { AuditUserRole } from '../dtos';

/**
 * Creates data management hooks for READY status survey audits
 * These are audits that have been auto-created but not yet started
 *
 * @param subjectId - Subject ID for scoping audits
 * @param tenantId - Tenant ID (for context, may not be used directly)
 * @param domainId - Domain ID (for context, may not be used directly)
 */
export const createReadyAuditsDataManagementHooks = (
  subjectId: string,
  _tenantId: string,
  _domainId: string
) => {
  /**
   * Query hook: Get READY and IN_PROGRESS audits from backend
   * Backend enriches with surveyTitle, responseCount, and questionCount
   * Includes both READY (not started) and IN_PROGRESS (started but not completed) audits
   */
  const useReadyAuditsQuery = () => {
    const query = useSurveyAuditsBySubject(subjectId, {
      status: ['READY', 'IN_PROGRESS'],
      enabled: !!subjectId,
    });

    return {
      ...query,
      // Direct passthrough - types already match due to alias
      data: query.data?.data?.audits || [],
      refetch: query.refetch,
      isFetching: query.isFetching,
    };
  };

  /**
   * Start audit mutation wrapper
   * Used to transition a READY audit to IN_PROGRESS
   */
  const useStartAuditWrapper = () => {
    const startMutation = useStartAudit();

    return {
      mutateAsync: async (params: {
        surveyInstanceId: string;
        userRole: AuditUserRole;
        surveyId: string;
      }): Promise<SurveyAuditTableData> => {
        const response = await startMutation.mutateAsync(params);

        if (!response.data) {
          throw new Error('Failed to start audit: no data returned');
        }

        return {
          id: response.data.id,
          surveyInstanceId: response.data.surveyInstanceId,
          surveyId: response.data.surveyId,
          tenantId: response.data.tenantId,
          domainId: response.data.domainId,
          subjectId: response.data.subjectId,
          status: response.data.status,
          auditedBy: response.data.auditedBy,
          auditedByRole: response.data.auditedByRole,
          surveyTitle: (response.data as any).surveyTitle ?? '',
          responseCount: (response.data as any).responseCount ?? 0,
          questionCount: (response.data as any).questionCount ?? 0,
          startedAt: response.data.startedAt,
          completedAt: response.data.completedAt,
          publishedAt: response.data.publishedAt,
          isLocked: response.data.isLocked,
          createdAt: response.data.createdAt,
          updatedAt: response.data.updatedAt,
        };
      },
      isPending: startMutation.isPending,
    };
  };

  /**
   * Archive audit mutation wrapper
   * Used to archive a READY audit (READY → ARCHIVED)
   */
  const useArchiveAuditWrapper = () => {
    return useArchiveAudit();
  };

  return {
    useReadyAuditsQuery,
    useStartAuditWrapper,
    useArchiveAuditWrapper,
  };
};
